import { Modal } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import { clsx } from 'clsx';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import { ForgotPasswordModalContent } from '@jane/bloom/components/accounts/forgotPasswordModalContent';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { useScrollInputsIntoView } from '@jane/bloom/hooks/useScrollInputsIntoView';
import type { CheckoutAuthFormStep } from '@jane/bloom/routes/_menu.$basename.checkout._index/types';

import styles from './loginModal.module.css';
import { SignIn } from './signIn';
import { SignUp } from './signUp';
import { Welcome } from './welcome';

const resetEmailCopy =
  'Check your email for a link to reset your password from Jane.';

interface LoginModalProps {
  googleSsoClientId?: Nullable<string>;
  isBloomMessagingForMigratedUsersEnabled: boolean;
  setShowAuthModal: Dispatch<SetStateAction<boolean>>;
  showAuthModal: boolean;
  skipBagAfterAuth: boolean;
}

export const LoginModal = ({
  googleSsoClientId,
  isBloomMessagingForMigratedUsersEnabled,
  setShowAuthModal,
  showAuthModal,
  skipBagAfterAuth,
}: LoginModalProps) => {
  useScrollInputsIntoView();
  const [userEmail, setUserEmail] = useState('');
  const [formStep, setFormStep] = useState<CheckoutAuthFormStep>('signIn');

  const close = () => {
    setShowAuthModal(false);
    setUserEmail('');
    setFormStep('signIn');
  };

  return (
    <ClientOnly>
      {() => (
        <Modal
          appId="jane-container"
          variant="dialogue"
          onRequestClose={close}
          open={showAuthModal}
          className={clsx(styles['modal'], {
            [styles['modal--login']]: formStep !== 'createAccount',
            [styles['modal--signup']]: formStep === 'createAccount',
          })}
          parentId="auth-modal"
          overlayClose
        >
          {formStep === 'signIn' && (
            <SignIn
              googleSsoClientId={googleSsoClientId}
              userEmail={userEmail}
              setFormStep={setFormStep}
              setUserEmail={setUserEmail}
              onClose={close}
              skipBagAfterAuth={skipBagAfterAuth}
            />
          )}

          {formStep.includes('existingUser') && (
            <Welcome
              googleSsoClientId={googleSsoClientId}
              skipBagAfterAuth={skipBagAfterAuth}
              userEmail={userEmail}
              setFormStep={setFormStep}
              onClose={close}
              isBloomMessagingForMigratedUsersEnabled={
                isBloomMessagingForMigratedUsersEnabled
              }
              bottomText={
                formStep === 'existingUserAfterReset'
                  ? resetEmailCopy
                  : undefined
              }
            />
          )}

          {formStep === 'createAccount' && (
            <SignUp
              userEmail={userEmail}
              onClose={close}
              skipBagAfterAuth={skipBagAfterAuth}
              googleSsoEnabled={!!googleSsoClientId}
            />
          )}

          {formStep === 'forgotPassword' && (
            <ForgotPasswordModalContent
              userEmail={userEmail}
              setFormStep={setFormStep}
              onClose={close}
            />
          )}
        </Modal>
      )}
    </ClientOnly>
  );
};
